import { useEffect, useMemo, useRef, useState } from 'react';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useTour } from '@/contexts/useTour';
import { usePermissions } from '@/contexts/PermissionContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import { useAiWarnings } from '@/hooks/useAiWarnings';
import { useColumnResize } from '@/hooks/useColumnResize';
import { rejectionService } from '@/services/rejectionService';
import { DEFAULT_TIMESHEET_SETTINGS } from '@/types/timesheetSettings';
import type { TimesheetEntry } from '@/types/timesheet';
import { getEffectiveDescriptionType } from '@/utils/descriptionTypeUtils';
import { buildDaysFromStart, getNonWorkingDaysForEmployee } from '../timesheetGridUtils';
import { useTimesheetData } from './useTimesheetData';
import { useTimesheetWeekState, type TimesheetDrilldownState } from './useTimesheetWeekState';
import { useTimesheetOvertime } from './useTimesheetOvertime';
import { useTimesheetTotals } from './useTimesheetTotals';
import { useTimesheetValidation } from './useTimesheetValidation';

export type { TimesheetDrilldownState } from './useTimesheetWeekState';

/** The subset of the permission context the timesheet grid depends on. */
export interface TimesheetGridPermissions {
  canDeleteTimesheet: () => boolean;
  canDuplicateTimesheet: () => boolean;
  canRecallTimesheet: () => boolean;
  canCopyFromLastWeek: () => boolean;
  canUseSmartFill: () => boolean;
  canUseAiInsights: () => boolean;
}

export interface UseTimesheetGridCoreParams {
  navigationState?: TimesheetDrilldownState;
}

/**
 * Grid foundations: contexts, the week being viewed, the entries being edited and
 * every value derived from them (settings, projects, totals, overtime, validation).
 */
export function useTimesheetGridCore({ navigationState }: UseTimesheetGridCoreParams) {
  const {
    projects,
    costCentres,
    currentEmployee,
    employees,
    timesheets,
    updateTimesheet,
    updateCostCentreLastUsed,
    refreshEmployeeTimesheets,
  } = useTimesheetForScreen('timesheet');
  const { getScreenMode } = useDataSource();
  const { startPendingTour } = useTour();
  const {
    canEditTimesheet,
    canDeleteTimesheet,
    canDuplicateTimesheet,
    canRecallTimesheet,
    canCopyFromLastWeek,
    canUseSmartFill,
    canUseAiInsights,
  } = usePermissions();
  const permissions: TimesheetGridPermissions = {
    canDeleteTimesheet,
    canDuplicateTimesheet,
    canRecallTimesheet,
    canCopyFromLastWeek,
    canUseSmartFill,
    canUseAiInsights,
  };
  const { weekStartDay } = useSettings();
  const { currentCompany } = useCompany();

  // Get current screen mode for workflow integration
  const screenMode = getScreenMode('timesheet');

  // Determine which employee's timesheet to display.
  // Employee.id is now a number type matching the backend INT type
  const targetEmployeeId = navigationState?.employeeId ?? currentEmployee.id;

  // When drilling into another resource's timesheet from a report, we lock the
  // week to the period that was clicked. Week navigation (prev/next/date-picker)
  // would silently fall back to the logged-in user's context for weeks that have
  // no timesheet for the target employee, producing "timesheet already exists"
  // API errors and confusion about whose timesheet is being edited.
  const isViewingOtherResource =
    navigationState?.employeeId != null && navigationState.employeeId !== currentEmployee.id;

  // Check for pending tours when component mounts
  useEffect(() => {
    startPendingTour();
  }, [startPendingTour]);

  const { currentWeekStart, setCurrentWeekStart, currentTimesheet, viewedResourceName } = useTimesheetWeekState({
    navigationState,
    weekStartDay,
    screenMode,
    targetEmployeeId,
    currentEmployee,
    employees,
    timesheets,
    currentCompany,
    isViewingOtherResource,
    refreshEmployeeTimesheets,
  });

  const [entries, setEntries] = useState<TimesheetEntry[]>(currentTimesheet.entries);

  // ── Auto-save & dirty tracking ──────────────────────────────────────────
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);

  // Get rejection history for current timesheet
  const rejectionHistory = useMemo(() => {
    if (!currentTimesheet.id || currentTimesheet.id === 0) {
      return null;
    }
    return rejectionService.getTimesheetRejectionHistory(currentTimesheet.id);
  }, [currentTimesheet.id]);

  // Get the most recent pending rejection
  const pendingRejection = useMemo(() => {
    if (!rejectionHistory || rejectionHistory.rejections.length === 0) {
      return null;
    }
    return rejectionHistory.rejections.find(r => r.status === 'pending_correction') || null;
  }, [rejectionHistory]);

  // Whether the current user is permitted to edit the timesheet being viewed.
  // When drilling into another resource's timesheet from the Timesheet Report,
  // the Edit access level (None / Own / Team / All) determines editability. If
  // the user lacks edit rights for this employee, the grid is view-only so the
  // fields are inactive rather than editable-then-403-on-save.
  const canEditThisTimesheet = canEditTimesheet(targetEmployeeId);

  const isReadOnly = !canEditThisTimesheet ||
    (currentTimesheet.status !== 'draft' && currentTimesheet.status !== 'rejected' && currentTimesheet.status !== 'recalled');
  const isRejected = currentTimesheet.status === 'rejected' && pendingRejection;
  const isSubmitted = currentTimesheet.status === 'submitted';

  // All side-effecting data loading: organisation settings, working pattern,
  // every project source, scheduled bookings and absences.
  const data = useTimesheetData({
    screenMode,
    targetEmployeeId,
    currentEmployee,
    currentWeekStart,
    companyId: currentCompany?.id,
    projects,
    costCentres,
    timesheets,
    entries,
    setEntries,
    isReadOnly,
    autoSaveTimerRef,
  });
  const { organizationSettings, employeeWorkingPattern } = data;

  // Always use the COMPANY (org) working pattern for grid visibility so that
  // days the company considers working always appear — even when the employee's
  // Resource Manager pattern marks them as non-working.  Those extra days are
  // styled as non-working and trigger the Non-Working Day Entry policy check.
  const companyWorkingDays = useMemo(() => {
    return organizationSettings?.workingProfile?.workingDays || DEFAULT_TIMESHEET_SETTINGS.workingProfile.workingDays;
  }, [organizationSettings?.workingProfile?.workingDays]);

  const { days: DAYS, labels: DAY_LABELS } = useMemo(() => {
    return buildDaysFromStart(weekStartDay, companyWorkingDays);
  }, [weekStartDay, companyWorkingDays]);

  // Days that are working for the company but non-working for the employee
  // (different Resource Manager pattern). Shown but styled distinctly.
  const nonWorkingDays = useMemo(() => {
    return getNonWorkingDaysForEmployee(companyWorkingDays, employeeWorkingPattern);
  }, [companyWorkingDays, employeeWorkingPattern]);

  // Sync entries when the timesheet changes (id or entries from context), and when
  // currentWeekStart changes — that catches the case where both old and new week
  // have id=0 (empty timesheets) so neither the id nor the stable EMPTY_ENTRIES
  // reference changes, but we must still reset to the correct week's entries.
  useEffect(() => {
    setEntries(currentTimesheet.entries);
    data.clearFetchedCostCentresCache();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeekStart, currentTimesheet.id, currentTimesheet.entries]);

  // Reset the scheduled-bookings flag only when the week or timesheet identity
  // changes — NOT when entries change due to a mid-navigation save. This
  // prevents flushSync(updateTimesheet) inside navigateWeek from prematurely
  // resetting the flag before the week has actually changed.
  useEffect(() => {
    data.setHasLoadedScheduledBookings(false);
    data.setHasLoadedAbsences(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeekStart, currentTimesheet.id]);

  // Use the fetched employee working pattern (from working patterns API),
  // falling back to the pattern on the employee object (from /employees API, if populated)
  const effectiveWorkingPattern = employeeWorkingPattern || currentEmployee?.workingPattern;

  // AI anomaly warnings for the current week + user dismissals and blocking checks.
  const aiWarningsState = useAiWarnings({
    entries,
    currentWeekStart,
    timesheets,
    effectiveWorkingPattern,
    organizationSettings,
  });

  // DAYS already contains only working days in the correct order from buildDaysFromStart
  const visibleDays = DAYS;

  const totals = useTimesheetTotals({ entries, visibleDays });
  const overtime = useTimesheetOvertime({
    entries,
    visibleDays,
    organizationSettings,
    employeeWorkingPattern,
  });

  const isDaily = getEffectiveDescriptionType(
    organizationSettings?.descriptionType ?? 'row',
    currentTimesheet.status,
    currentTimesheet.entries,
  ) === 'daily';

  const validation = useTimesheetValidation({
    entries,
    visibleDays,
    organizationSettings,
    isDaily,
    companyHasCostingIntegration: currentCompany?.hasCostingIntegration === true,
  });

  // Number of columns rendered in a grid row, including the trailing spacer column
  const gridColumnCount =
    1 + // Project
    (organizationSettings?.showCostCentre !== false ? 1 : 0) +
    (organizationSettings?.showRole ? 1 : 0) +
    (overtime.overtimeEnabled ? 1 : 0) +
    (!isDaily ? 1 : 0) +
    visibleDays.length +
    2 + // Total, Actions
    1; // spacer

  // Resizable non-hours columns. Widths are persisted so the layout survives reloads;
  // columns the user has not resized share whatever width the fixed columns leave over.
  const columnResize = useColumnResize({
    tableScrollRef,
    layoutKey: [
      organizationSettings?.showCostCentre !== false,
      organizationSettings?.showRole,
      overtime.overtimeEnabled,
      isDaily,
      visibleDays.length,
      entries.length,
    ].join('|'),
  });

  return {
    // Context values
    projects,
    costCentres,
    currentEmployee,
    employees,
    timesheets,
    updateTimesheet,
    updateCostCentreLastUsed,
    permissions,
    screenMode,
    weekStartDay,
    currentCompany,
    // Week + timesheet identity
    targetEmployeeId,
    isViewingOtherResource,
    viewedResourceName,
    currentWeekStart,
    setCurrentWeekStart,
    currentTimesheet,
    // Entries + persistence bookkeeping
    entries,
    setEntries,
    lastSaved,
    setLastSaved,
    autoSaveTimerRef,
    tableScrollRef,
    // Status flags
    pendingRejection,
    canEditThisTimesheet,
    isReadOnly,
    isRejected,
    isSubmitted,
    // Derived data
    data,
    organizationSettings,
    effectiveWorkingPattern,
    aiWarningsState,
    visibleDays,
    dayLabels: DAY_LABELS,
    nonWorkingDays,
    totals,
    overtime,
    validation,
    isDaily,
    gridColumnCount,
    columnResize,
  };
}

export type TimesheetGridCore = ReturnType<typeof useTimesheetGridCore>;
